import {ProcedureParameterConfirmationMethod} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ProcedureParameterSchemaFieldName} from 'notebook/integrations/procedures/ParameterStructuredTableConstants';
import {EvaluatedSimpleTableDoc} from 'notebook/simple-tables/useSimpleEvaluation';
import {TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {
  BioentityLinkSchemaFieldTypedValue,
  FloatSchemaFieldTypedValue,
} from 'notebook/tables/typed-values/TypedValueTypes';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {BaseCondition} from 'studies/typed/shared/procedure-runs/design-state/useExperimentDesignState';

// Represents any method component (parameter, material input/output, equipment)
export interface BasePlanComponentDataType {
  // TODO(BNCH-87720): Clean up. `name` is being used until M1 data model is fully implemented.
  // `sourceId` is still used for M0 and we may switch back to it for M1.
  name: string; // name of the component
  sourceId: string; // id of the component
  values: Array<TypedValue>;
}

export interface PlannedMethodInstanceValues {
  methodId: string;
  parameterValues: Array<BasePlanComponentDataType>;
  materialInputs: Array<BasePlanComponentDataType>;
  equipmentInputs: Array<BasePlanComponentDataType>;
}

export interface FullExperimentPlan {
  replicatesPerCondition: Array<number>;
  namesPerCondition: Array<string>;
  descriptionsPerCondition: Array<string | undefined>;
  experimentPlanPerCondition: Array<Array<PlannedMethodInstanceValues>>;
  // Confirmation method changes are applied across all conditions. Example data:
  // {schver_10djs9: ProcedureParameterConfirmationMethod.TABLE}
  confirmationMethodByParamVersionId: Record<string, ProcedureParameterConfirmationMethod>;
}

export enum ExperimentalInputsComponentName {
  PARAMETERS = 'parameters',
  MATERIAL_INPUTS = 'materialInputs',
  EQUIPMENT = 'equipment',
}

// Note that this type requires all `ComponentNames` to be present as keys
export type ExperimentalInputsByComponentName = Record<ExperimentalInputsComponentName, Array<string>>;

export type ExperimentalInputTypedValuesByComponentName = Record<
  ExperimentalInputsComponentName,
  ParameterFieldValues
>;
export type ExperimentalInputsValuesPerCondition = Array<ExperimentalInputTypedValuesByComponentName>;

/** A name for a particular parameter can be accessed via `parameterNames[parameterIndex]` */
type ParameterNames = Array<string>;
/** An id for a particular parameter can be accessed via `ids[parameterIndex]` */
type ParameterSettingIds = Array<string>;
/** A value for a particular field of a parameter can be accessed via `values[parameterIndex][fieldIndex]` */
export type ParameterFieldValues = Array<Array<TypedValue>>;

export interface ParameterField {
  schemaField: SchemaField;
  displayName: string;
  type: ProcedureParameterSchemaFieldName;
  maximum?: FloatSchemaFieldTypedValue;
  minimum?: FloatSchemaFieldTypedValue;
  allowedEquipmentModels?: BioentityLinkSchemaFieldTypedValue;
  /**
   * If the field is confirmed in a plate map, treat this field as a multi-value field.
   * Has no effect if the field is confirmed in a table.
   *
   * If true, update serializeExperimentConditionsForNetwork to expand the multi-valued typed value into
   * multiple scalar typed values.
   */
  allowMultiValueForPlateMap: boolean;
}

/**
 * A particular field can be accessed via `parameterFields[parameterIndex][fieldIndex]`.
 */
export type ParameterFields = Array<Array<ParameterField>>;

export interface ExperimentalInputsPerMethod {
  values: ExperimentalInputsValuesPerCondition;
  parameterNames: Record<ExperimentalInputsComponentName, ParameterNames>;
  // Confirmation method changes are applied across all conditions.
  parameterConfirmationMethods: Record<
    ExperimentalInputsComponentName,
    Array<ProcedureParameterConfirmationMethod>
  >;
  defaultValues: ExperimentalInputTypedValuesByComponentName;
  ids: Record<ExperimentalInputsComponentName, ParameterSettingIds>;
  // Will be empty array for non-parameters
  units: ExperimentalInputsByComponentName;
  fields: Record<ExperimentalInputsComponentName, ParameterFields>;
}

// Tracks the user's actual data entry
export interface ExperimentalInputs {
  [methodId: string]: ExperimentalInputsPerMethod;
}

export interface ExperimentalInputEvaluatedValues {
  [methodId: string]: {
    /**
     * The number of cells with blocking validation errors across all tables for this method.
     */
    blockingValueErrorCount: number;
    /**
     * Values after evaluation / typed value resolution.
     */
    values: ExperimentalInputsValuesPerCondition;
  };
}

// This allows the top-level modal to hydrate all downstream components with an easy way to get/set the inputs
export interface ExperimentalInputsContextProperties {
  conditions: Array<BaseCondition>;
  /** Whether archived condition columns appear in input tables. */
  showArchivedConditions?: boolean;
  draftInputs: ExperimentalInputs;
  setExperimentalInputs: (
    methodId: string,
    newInputs: ExperimentalInputsValuesPerCondition,
    newConfirmationMethods: Record<
      ExperimentalInputsComponentName,
      Array<ProcedureParameterConfirmationMethod>
    >
  ) => void;
  locked: boolean;
  simpleTableDoc: EvaluatedSimpleTableDoc;
  evaluatedValues: ExperimentalInputEvaluatedValues;
}
